import { ENTITY_TYPE_MAPPINGS } from "./sharedConstants"
import moment from "moment"
import { formatRoute as namedRoutesFormatRoute } from "react-router-named-routes"
import routes from "../routes"
import { createMatchSelector } from "react-router-redux"
require("moment-duration-format") // not called directly, but extends moment to add moment.duration()

export const getEntityTypeMapping = key =>
  ENTITY_TYPE_MAPPINGS.find(mapping => {
    return Object.values(mapping).includes(key)
  })

export const titleize = string => {
  return string && string.charAt(0).toUpperCase() + string.slice(1)
}

export const visitText = rowData => {
  const visitDate = moment.parseZone(rowData.entryAt)
  const duration = moment.duration(rowData.duration, "seconds")

  return {
    date: visitDate.format("MMM D"),
    dotDivider: "•",
    time: visitDate.format("h:mm a").slice(0, -1),
    duration: duration.format("(h[h] m[m])"),
  }
}

// This function will take in the route "/:entityType(facility|department)/:entityId"
// and treat it as "/:entityType/:entityId" when sending it to formatRoute, since
// react-router-named-routes doesn't know how to support custom match parameters.
export const formatRoute = (route, params) => {
  const strippedRoute = route.replace(/\([^)]*\)/g, "") // remove custom match params
  return namedRoutesFormatRoute(strippedRoute, params)
}

export const maybePluralize = (count, singularSubject, pluralSubject) => {
  if (pluralSubject) {
    return `${count.toLocaleString()} ${
      count !== 1 ? pluralSubject : singularSubject
    }`
  } else {
    return `${count.toLocaleString()} ${singularSubject}${
      count !== 1 ? "s" : ""
    }`
  }
}

// This function can be written in a more concise way, but it becomes a pain to actually follow the logic.
// It's much easier to just enumerate all the entityTypes and explicitly generate a path for each one.
export const nestedEntityPath = ({
  parentEntityType,
  parentEntityId,
  entityType,
  entityId,
}) => {
  parentEntityType = getEntityTypeMapping(parentEntityType).singularRoute
  entityType = getEntityTypeMapping(entityType).singularRoute

  switch (entityType) {
    case "department":
    case "unit":
      return formatRoute(routes.nestedEntityOverview, {
        parentEntityType,
        parentEntityId,
        entityType,
        entityId,
      })
    case "role":
      return formatRoute(routes.nestedEntityDetail, {
        parentEntityType,
        parentEntityId,
        entityType,
        entityId,
        childEntityType: "staff",
      })
    case "staff":
    case "room":
      return formatRoute(routes.entityDetail, {
        parentEntityType,
        parentEntityId,
        entityType,
        entityId,
      })
    /* istanbul ignore next */
    default:
      throw new Error(`Unexpected entityType: ${entityType}`)
  }
}

export const getMatchFromRouter = router => {
  const routeKeys = Object.keys(routes)
  for (let i = 0; i < routeKeys.length; i++) {
    const key = routeKeys[i]
    const match = createMatchSelector(routes[key])({ router })
    if (match && match.isExact) {
      return { key, ...match }
    }
  }
}

export const secondsToHoursAndMinutes = seconds => {
  return (
    Math.floor(seconds / 3600) + "h " + Math.round((seconds / 60) % 60) + "m"
  )
}

export const noOccupancy = shift => {
  return (
    shift &&
    (shift.compliancePercentage === null ||
      shift.compliancePercentage === undefined)
  )
}

export const noVisitsDetected = shift => {
  return (
    shift &&
    noRNDetected(shift) &&
    !shift.secondaryUser &&
    shift.compliancePercentage === 0
  )
}

export const noRNDetected = shift => {
  return shift && !shift.primaryUser
}

export const complianceAmount = value => {
  if (value >= 75) {
    return "success-progress"
  }
  if (value < 75 && value >= 50) {
    return "improvement-progress"
  }
  if (value < 50 && value >= 25) {
    return "warning-progress"
  } else {
    return "danger-progress"
  }
}

export const complianceAmountColor = value => {
  if (value >= 75) {
    return "#07c180"
  }
  if (value < 75 && value >= 50) {
    return "#c1d349"
  }
  /* istanbul ignore next */
  if (value < 50 && value >= 25) {
    return "#ffb649"
  } else {
    return "#ff3b2d"
  }
}

export const getBool = value => {
  if (value > 0) {
    return true
  } else {
    return false
  }
}

export const evaluateRoundingStatus = location => {
  if (location.lastRounded === null) return "danger-rounding"

  const elapsedTime = moment().diff(moment(location.lastRounded), "minutes")

  const timeLeftToRoundLocation = location.roundingDurationMinutes - elapsedTime

  if (timeLeftToRoundLocation > 20) {
    return "success-rounding"
  } else if (timeLeftToRoundLocation <= 20 && timeLeftToRoundLocation > 0) {
    return "warning-rounding"
  } else if (timeLeftToRoundLocation <= 0) {
    return "danger-rounding"
  }
}

export const getResolutionHeight = () => {
  const height = window.screen.height
  if (height > 1440) {
    return "xl-res"
  } else if (height <= 1440 && height > 1080) {
    return "lg-res"
  } else if (height <= 1080 && height > 720) {
    return "md-res"
  } else {
    return "sm-res"
  }
}
